import { joinWithConjunction, pluralise } from 'helpers/string.helpers';
import type {
  ManageMarkingMark,
  ManageMarkingMarkItem
} from 'services/assist-api/entities/test-instance-response-mark/types';

/**
 * Returns all marks that the marker is the active marker of.
 */
export function markersMarks(
  markerId: number,
  marksOfResponse: ManageMarkingMark[]
): ManageMarkingMark[] {
  return marksOfResponse.filter((mark) => mark.activeMarker?.id === markerId);
}

/**
 * Returns all mark items that the marker is the active marker of.
 */
export function markersMarkItems(
  markerId: number,
  marksOfResponse: ManageMarkingMark[]
): ManageMarkingMarkItem[] {
  return marksOfResponse.flatMap(
    (mark) => mark.markItems?.filter((mi) => mi.testInstanceResponseMarker?.id === markerId) || []
  );
}

/**
 * Returns all marks and mark items that the marker is the active marker of. If the marker is a
 * partial marker of a mark (the active marker of mark items only), include that mark in the
 * collection, as they are a contributor of the mark.
 */
export function markersContributingMarksAndMarkItems(
  markerId: number,
  marksOfResponse: ManageMarkingMark[]
): ManageMarkingMark[] {
  const contributingMarks = marksOfResponse.filter(
    (mark: ManageMarkingMark) =>
      mark.activeMarker?.id === markerId || markersMarkItems(markerId, [mark]).length > 0
  );
  return contributingMarks.map((mark) => ({
    ...mark,
    markItems: markersMarkItems(markerId, [mark])
  }));
}

/**
 * Returns true if the marker is the active marker of a mark or a child mark item in the
 * marksOfResponse array.
 */
export function isAnActiveMarkerOfResponse(
  markerId: number,
  marksOfResponse: ManageMarkingMark[]
): boolean {
  return (
    markersMarkItems(markerId, marksOfResponse).length > 0 ||
    markersMarks(markerId, marksOfResponse).length > 0
  );
}

/**
 * Returns the marker IDs of the active markers that have contributed to the specified marks, either
 * as the active marker of a mark or a mark item.
 */
export function uniqueMarkerIdsOfMarksAndMarkItems(marks: ManageMarkingMark[]): number[] {
  const markIds = marks.map((mark) => mark.activeMarker.id);
  const markItemIds: number[] = marks.flatMap(
    (mark) => mark.markItems?.map((mi) => mi.testInstanceResponseMarker?.id) || []
  );
  return [...new Set(markIds.concat(markItemIds))];
}

/**
 * Displays count messages for marks and mark items that the intended marker has marked, when either
 * or both of these counts are greater than 0.
 */
export function countMessages(
  intendedMarkerId: number,
  marksOfResponse: ManageMarkingMark[]
): string {
  const markCount = markersMarks(intendedMarkerId, marksOfResponse).length;
  const markItemCount = markersMarkItems(intendedMarkerId, marksOfResponse).length;
  const messages: string[] = [];
  if (markCount > 0) {
    messages.push(`${markCount} ${pluralise('mark', markCount)}`);
  }
  if (markItemCount > 0) {
    messages.push(`${markItemCount} scored ${pluralise('item', markItemCount)}`);
  }
  return joinWithConjunction(messages, 'and');
}
